import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';
import {
  BenchmarkSkillsAverageReport
// eslint-disable-next-line max-len
} from '@shared/branded-reports/branded-benchmark-reports/admin-skills-average-report/skills-average-report-detail.model';
import { BarColors, BorderColors, TextColors } from '@shared/branded-reports/zbchart-report/bar-colors.enum';
import { ZbChartReportTableComponent } from '@shared/branded-reports/zbchart-report/zbchart-report-table.component';
import { ZbChartBar, ZbChartBarValue, ZbChartData } from '@shared/branded-reports/zbchart-report/zbchart-report.model';
import { ZbpDataTable } from '@shared/zbp-data-table/zbp-data-table.model';
import PageChangeEvent = ZbpDataTable.PageChangeEvent;

@Component({
  selector: 'zbp-admin-skills-average-detail',
  imports: [
    ZbChartReportTableComponent,
    NgbPagination
  ],
  templateUrl: './admin-skills-average-detail.component.html',
  styleUrl: './admin-skills-average-detail.component.scss'
})
export class AdminSkillsAverageDetailComponent implements OnChanges {

  @Input() benchmarkSkillsAverageReport?: BenchmarkSkillsAverageReport;

  /** See {@link NgbPagination#pageSize}. */
  @Input() pageSize: number = 25;

  /** See {@link NgbPagination#maxSize}. */
  @Input() maxPageCount: number = 10;

  /** See {@link NgbPagination#collectionSize}. */
  @Input() collectionSize: number;

  /** See {@link NgbPagination#page}. */
  @Input() page: number;

  @Output() pageChange = new EventEmitter<PageChangeEvent>();

  @ViewChild('pagination') pagination: NgbPagination;

  zbChart: ZbChartData;

  statusMessage = '';
  numberOfDisplayedRecords: number;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.benchmarkSkillsAverageReport) {
      this.createZbChart(changes.benchmarkSkillsAverageReport.currentValue);
      this.updateStatusMessage(changes.benchmarkSkillsAverageReport.currentValue);
    }
  }

  createZbChart(
    report: BenchmarkSkillsAverageReport,
  ): ZbChartData {
    if (!report) {
      return null;
    }

    const chartData = { bars: [] } as ZbChartData;
    chartData.showZeroCount = true;

    this.numberOfDisplayedRecords = report.benchmarkResults.length;

    report.benchmarkResults.forEach((result) => {
      const row = {} as ZbChartBar;
      const values = [] as ZbChartBarValue[];

      row.label = result.averageDisplayName;
      row.labelOnLeft = false;

      row.totalCount = 100;
      row.ariaLabel = `Average score for this ${report.entityViewType} is ${result.standardAverageFormatted}`;
      values.push({
        barLabel: result.standardAverageFormatted,
        barTextColor: TextColors.black,
        color: this.determineBarColor(result.standardAverage),
        borderColor: this.determineBorderColor(result.standardAverage),
        percent: result.standardAverage,
        count: result.standardAverage,
      });

      row.values = values;
      chartData.bars.push(row);
    });

    this.zbChart = chartData;

    return chartData;
  }

  determineBarColor(percent: number): BarColors {
    let color: BarColors;
    if (!percent) {
      color = null;
    } else if (percent < 70) {
      color = BarColors.red300;
    } else if (percent < 90) {
      color = BarColors.cream300;
    } else {
      color = BarColors.green300;
    }
    return color;
  }

  determineBorderColor(percent: number): BorderColors {
    let color: BorderColors;
    if (!percent) {
      color = null;
    } else if (percent < 70) {
      color = BorderColors.borderForRed300;
    } else if (percent < 90) {
      color = BorderColors.borderForCream300;
    } else {
      color = BorderColors.borderForGreen300;
    }
    return color;
  }

  private updateStatusMessage(report: BenchmarkSkillsAverageReport) {
    this.statusMessage = '';
    let resultsMessage = '';
    if (report && report.benchmarkResults.length !== 0) {
      const resultWord = report.benchmarkResults.length === 1 ? 'result' : 'results';
      resultsMessage = `${report.benchmarkResults.length} ${resultWord} found.`;
    } else {
      resultsMessage = 'No results found.';
    }
    setTimeout(() => {
      this.statusMessage = resultsMessage;
    }, 100);
  }

  onPageChange(page: number) {
    this.pageChange.emit({ page, pagination: this.pagination });
  }

}
